import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import {
  TrendingUp, Users, Clock, CheckCircle, XCircle, AlertCircle, Plus,
  Calendar, DollarSign, FileText, History, Award, ArrowUpRight,
} from "lucide-react";
import { format } from "date-fns";
import api from "../lib/api";
import { formatCurrency } from "../lib/currency";

const Appraisals = () => {
  const { currentBusiness } = useAuth();
  const fmt = (amount) => formatCurrency(amount, currentBusiness?.currency);
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("due");
  const [stats, setStats] = useState({});
  const [dueEmployees, setDueEmployees] = useState({ due_now: [], upcoming: [] });
  const [appraisals, setAppraisals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [performanceReviews, setPerformanceReviews] = useState([]);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [incrementHistory, setIncrementHistory] = useState([]);
  
  // Form states
  const [formData, setFormData] = useState({
    employee_id: "",
    appraisal_year: new Date().getFullYear(),
    current_salary: 0,
    proposed_increment_percent: 0,
    proposed_new_salary: 0,
    performance_review_id: "",
    remarks: "",
    effective_date: "",
  });
  
  const [approvalData, setApprovalData] = useState({
    final_increment_percent: 0,
    final_new_salary: 0,
    effective_date: "",
    approval_remarks: "",
  });

  // Memoized filtered arrays for performance
  const approvedAppraisals = useMemo(() => 
    appraisals.filter(a => a.status === 'approved'),
    [appraisals]
  );
  
  const pendingAppraisals = useMemo(() => 
    appraisals.filter(a => a.status === 'pending'),
    [appraisals]
  );

  useEffect(() => {
    if (currentBusiness?.id) {
      fetchData();
    }
  }, [currentBusiness]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, dueRes, appraisalsRes, employeesRes] = await Promise.all([
        api.get(`/appraisals/stats?business_id=${currentBusiness.id}`),
        api.get(`/appraisals/due?business_id=${currentBusiness.id}&months_ahead=3`),
        api.get(`/appraisals?business_id=${currentBusiness.id}&year=${new Date().getFullYear()}`),
        api.get(`/employees?business_id=${currentBusiness.id}`),
      ]);
      
      setStats(statsRes.data);
      setDueEmployees(dueRes.data);
      setAppraisals(appraisalsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAppraisal = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/appraisals?business_id=${currentBusiness.id}`, formData);
      toast.success("Appraisal created successfully");
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create appraisal");
    }
  };

  const handleApprove = async () => {
    if (!selectedAppraisal) return;
    try {
      await api.put(`/appraisals/${selectedAppraisal.id}/approve`, approvalData);
      toast.success("Appraisal approved and salary updated");
      setApproveDialogOpen(false);
      setSelectedAppraisal(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve appraisal");
    }
  };

  const handleReject = async (appraisalId) => {
    try {
      await api.put(`/appraisals/${appraisalId}/reject`);
      toast.success("Appraisal rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject appraisal");
    }
  };

  const handleGenerateLetter = async (appraisalId) => {
    try {
      const response = await api.post(`/appraisals/${appraisalId}/letter`, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `appraisal_letter_${appraisalId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Letter generated");
    } catch (error) {
      toast.error("Failed to generate letter");
    }
  };

  const handleViewHistory = async (employeeId) => {
    try {
      const res = await api.get(`/appraisals/history/${employeeId}`);
      setIncrementHistory(res.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error("Failed to fetch history");
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: "",
      appraisal_year: new Date().getFullYear(),
      current_salary: 0,
      proposed_increment_percent: 0,
      proposed_new_salary: 0,
      performance_review_id: "",
      remarks: "",
      effective_date: "",
    });
  };

  const startAppraisalForEmployee = (emp) => {
    setFormData({
      ...formData,
      employee_id: emp.id,
      current_salary: emp.current_salary || 0,
      effective_date: emp.anniversary_date,
    });
    setCreateDialogOpen(true);
  };

  const openApprovalDialog = (appraisal) => {
    setSelectedAppraisal(appraisal);
    setApprovalData({
      final_increment_percent: appraisal.proposed_increment_percent || 0,
      final_new_salary: appraisal.proposed_new_salary || appraisal.current_salary,
      effective_date: appraisal.effective_date || "",
      approval_remarks: "",
    });
    setApproveDialogOpen(true);
  };

  const calculateNewSalary = (currentSalary, incrementPercent) => {
    return Math.round(currentSalary + (currentSalary * incrementPercent / 100));
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      in_review: "bg-blue-100 text-blue-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      not_started: "bg-slate-100 text-slate-800",
    };
    return <Badge className={styles[status] || styles.not_started}>{status?.replace('_', ' ').toUpperCase()}</Badge>;
  };

  const getUrgencyBadge = (urgency) => {
    if (urgency === 'overdue') return <Badge className="bg-red-100 text-red-800">OVERDUE</Badge>;
    if (urgency === 'due_now') return <Badge className="bg-orange-100 text-orange-800">DUE NOW</Badge>;
    return <Badge className="bg-blue-100 text-blue-800">UPCOMING</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appraisals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Yearly Appraisals</h1>
          <p className="text-slate-500">Manage employee appraisals and salary revisions</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-appraisal-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Appraisal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Appraisal</DialogTitle>
              <DialogDescription>Start a new appraisal for an employee</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateAppraisal} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Employee</Label>
                <Select value={formData.employee_id} onValueChange={(v) => {
                  const emp = employees.find(e => e.id === v);
                  setFormData({ ...formData, employee_id: v, current_salary: emp?.salary || 0 });
                }}>
                  <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} ({emp.employee_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Appraisal Year</Label>
                  <Input type="number" value={formData.appraisal_year}
                    onChange={(e) => setFormData({ ...formData, appraisal_year: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Current Salary</Label>
                  <Input type="number" value={formData.current_salary}
                    onChange={(e) => setFormData({ ...formData, current_salary: parseFloat(e.target.value) })} />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Proposed Increment (%)</Label>
                  <Input type="number" step="0.1" value={formData.proposed_increment_percent}
                    onChange={(e) => {
                      const pct = parseFloat(e.target.value) || 0;
                      setFormData({
                        ...formData,
                        proposed_increment_percent: pct,
                        proposed_new_salary: calculateNewSalary(formData.current_salary, pct)
                      });
                    }} />
                </div>
                <div className="space-y-2">
                  <Label>Proposed New Salary</Label>
                  <Input type="number" value={formData.proposed_new_salary}
                    onChange={(e) => setFormData({ ...formData, proposed_new_salary: parseFloat(e.target.value) })} />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Effective Date</Label>
                <Input type="date" value={formData.effective_date}
                  onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })} />
              </div>
              
              <div className="space-y-2">
                <Label>Remarks</Label>
                <Textarea value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  placeholder="Add any notes..." />
              </div>
              
              <Button type="submit" className="w-full btn-primary">Create Appraisal</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="card-modern">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-xl"><Clock className="h-5 w-5 text-yellow-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.pending || 0}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-modern">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-xl"><CheckCircle className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.approved || 0}</p>
                <p className="text-xs text-slate-500">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-modern">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-xl"><Users className="h-5 w-5 text-slate-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.not_started || 0}</p>
                <p className="text-xs text-slate-500">Not Started</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-modern">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-xl"><TrendingUp className="h-5 w-5 text-purple-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.average_increment_percent || 0}%</p>
                <p className="text-xs text-slate-500">Avg Increment</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="due">Due for Appraisal</TabsTrigger>
          <TabsTrigger value="all">All Appraisals</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
        </TabsList>

        {/* Due for Appraisal Tab */}
        <TabsContent value="due" className="space-y-4">
          {/* Due Now */}
          {dueEmployees.due_now?.length > 0 && (
            <Card className="card-modern">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                  Due Now ({dueEmployees.due_now.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Join Date</TableHead>
                        <TableHead>Anniversary</TableHead>
                        <TableHead>Years</TableHead>
                        <TableHead>Current Salary</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dueEmployees.due_now.map((emp) => (
                        <TableRow key={emp.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{emp.employee_name}</p>
                              <p className="text-xs text-slate-500">{emp.employee_code}</p>
                            </div>
                          </TableCell>
                          <TableCell>{emp.department}</TableCell>
                          <TableCell>{emp.join_date}</TableCell>
                          <TableCell>{emp.anniversary_date}</TableCell>
                          <TableCell>{emp.years_of_service} yrs</TableCell>
                          <TableCell>{fmt(emp.current_salary)}</TableCell>
                          <TableCell>
                            {getUrgencyBadge(emp.urgency)}
                            {emp.appraisal_status !== 'not_started' && (
                              <span className="ml-2">{getStatusBadge(emp.appraisal_status)}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {emp.appraisal_status === 'not_started' && (
                              <Button size="sm" onClick={() => startAppraisalForEmployee(emp)}>
                                <Plus className="h-4 w-4 mr-1" /> Start
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleViewHistory(emp.id)}>
                              <History className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming */}
          {dueEmployees.upcoming?.length > 0 && (
            <Card className="card-modern">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  Upcoming ({dueEmployees.upcoming.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Anniversary</TableHead>
                        <TableHead>Years</TableHead>
                        <TableHead>Current Salary</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dueEmployees.upcoming.map((emp) => (
                        <TableRow key={emp.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{emp.employee_name}</p>
                              <p className="text-xs text-slate-500">{emp.employee_code}</p>
                            </div>
                          </TableCell>
                          <TableCell>{emp.department}</TableCell>
                          <TableCell>{emp.anniversary_date}</TableCell>
                          <TableCell>{emp.years_of_service} yrs</TableCell>
                          <TableCell>{fmt(emp.current_salary)}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => startAppraisalForEmployee(emp)}>
                              Start Early
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {dueEmployees.due_now?.length === 0 && dueEmployees.upcoming?.length === 0 && (
            <Card className="card-modern">
              <CardContent className="py-8 text-center text-slate-500">
                No employees due for appraisal in the next 3 months
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* All Appraisals Tab */}
        <TabsContent value="all">
          <Card className="card-modern">
            <CardHeader>
              <CardTitle className="text-lg">All Appraisals - {stats.year}</CardTitle>
            </CardHeader>
            <CardContent>
              {appraisals.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No appraisals found for this year</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Current Salary</TableHead>
                        <TableHead>Proposed</TableHead>
                        <TableHead>Increment %</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appraisals.map((appraisal) => (
                        <TableRow key={appraisal.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{appraisal.employee_name}</p>
                              <p className="text-xs text-slate-500">{appraisal.employee_code}</p>
                            </div>
                          </TableCell>
                          <TableCell>{appraisal.department}</TableCell>
                          <TableCell>{fmt(appraisal.current_salary)}</TableCell>
                          <TableCell>{fmt(appraisal.proposed_new_salary || appraisal.final_new_salary)}</TableCell>
                          <TableCell>
                            <span className="flex items-center gap-1 text-green-600">
                              <ArrowUpRight className="h-4 w-4" />
                              {appraisal.proposed_increment_percent || appraisal.final_increment_percent}%
                            </span>
                          </TableCell>
                          <TableCell>{getStatusBadge(appraisal.status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {appraisal.status === 'pending' && (
                                <>
                                  <Button size="sm" onClick={() => openApprovalDialog(appraisal)}>
                                    <CheckCircle className="h-4 w-4 mr-1" /> Approve
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => handleReject(appraisal.id)}>
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              {appraisal.status === 'approved' && (
                                <Button size="sm" variant="outline" onClick={() => handleGenerateLetter(appraisal.id)}>
                                  <FileText className="h-4 w-4 mr-1" /> Letter
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approved Tab */}
        <TabsContent value="approved">
          <Card className="card-modern">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="h-5 w-5 text-green-500" />
                Approved Appraisals
              </CardTitle>
            </CardHeader>
            <CardContent>
              {approvedAppraisals.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No approved appraisals yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Previous Salary</TableHead>
                        <TableHead>New Salary</TableHead>
                        <TableHead>Increment</TableHead>
                        <TableHead>Effective Date</TableHead>
                        <TableHead>Approved By</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {approvedAppraisals.map((appraisal) => (
                        <TableRow key={appraisal.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{appraisal.employee_name}</p>
                              <p className="text-xs text-slate-500">{appraisal.employee_code}</p>
                            </div>
                          </TableCell>
                          <TableCell>{fmt(appraisal.current_salary)}</TableCell>
                          <TableCell className="font-medium text-green-600">{fmt(appraisal.final_new_salary)}</TableCell>
                          <TableCell>
                            <span className="flex items-center gap-1 text-green-600">
                              <ArrowUpRight className="h-4 w-4" />
                              {appraisal.final_increment_percent}%
                            </span>
                          </TableCell>
                          <TableCell>{appraisal.effective_date}</TableCell>
                          <TableCell>{appraisal.approved_by_name}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => handleGenerateLetter(appraisal.id)}>
                              <FileText className="h-4 w-4 mr-1" /> Download Letter
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Approval Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Approve Appraisal</DialogTitle>
            <DialogDescription>
              Confirm the final salary revision for {selectedAppraisal?.employee_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">Current Salary: <span className="font-medium">{fmt(selectedAppraisal?.current_salary)}</span></p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Final Increment (%)</Label>
                <Input type="number" step="0.1" value={approvalData.final_increment_percent}
                  onChange={(e) => {
                    const pct = parseFloat(e.target.value) || 0;
                    setApprovalData({
                      ...approvalData,
                      final_increment_percent: pct,
                      final_new_salary: calculateNewSalary(selectedAppraisal?.current_salary || 0, pct)
                    });
                  }} />
              </div>
              <div className="space-y-2">
                <Label>Final New Salary</Label>
                <Input type="number" value={approvalData.final_new_salary}
                  onChange={(e) => setApprovalData({ ...approvalData, final_new_salary: parseFloat(e.target.value) })} />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Effective Date</Label>
              <Input type="date" value={approvalData.effective_date}
                onChange={(e) => setApprovalData({ ...approvalData, effective_date: e.target.value })} />
            </div>
            
            <div className="space-y-2">
              <Label>Approval Remarks</Label>
              <Textarea value={approvalData.approval_remarks}
                onChange={(e) => setApprovalData({ ...approvalData, approval_remarks: e.target.value })}
                placeholder="Add any remarks..." />
            </div>
            
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800">
                Increment Amount: <span className="font-bold">{fmt((approvalData.final_new_salary || 0) - (selectedAppraisal?.current_salary || 0))}</span>
              </p>
            </div>
            
            <Button onClick={handleApprove} className="w-full btn-primary">
              <CheckCircle className="h-4 w-4 mr-2" /> Approve & Update Salary
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <History className="h-5 w-5" /> Increment History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {incrementHistory.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No increment history found</p>
            ) : (
              <div className="space-y-3">
                {incrementHistory.map((record) => (
                  <div key={record.id} className="p-3 border rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-slate-500">Year {record.appraisal_year}</p>
                        <p className="font-medium">{fmt(record.previous_salary)} → {fmt(record.new_salary)}</p>
                      </div>
                      <Badge className="bg-green-100 text-green-800">+{record.increment_percent}%</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Effective: {record.effective_date} | Approved by: {record.approved_by_name}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Appraisals;
